import type { OkBody } from '../Models/ApiModel';
import type {
  EnsembleDraftModel,
  EnsembleModel,
  EnsembleSlotModel,
  MemberChargeModel,
  ScheduledMeetingModel
} from '../Models/EnsembleModel';
import type { LessonModel } from '../Models/LessonModel';
import { http } from './httpService';

const path = (name: string, suffix = '') => `/ensembles/${encodeURIComponent(name)}${suffix}`;

/** Groups that learn together, and the meetings they turn into lessons. */
export const EnsembleService = {
  list: () => http.get<EnsembleModel[]>('/ensembles'),

  create: (ensemble: EnsembleDraftModel) => http.post<EnsembleModel>('/ensembles', ensemble),

  /**
   * A patch, so an unmentioned field keeps its stored value. A response
   * carrying `previousName` means the group was renamed and its members, hours
   * and lesson tags have moved with it.
   */
  update: (name: string, patch: EnsembleDraftModel) =>
    http.patch<EnsembleModel & { previousName?: string }>(path(name), patch),

  /** Deletes the group. The lessons it produced belong to the students and stay. */
  remove: (name: string) => http.delete<OkBody>(path(name)),

  /** Membership is a set, replaced wholesale: the editor is a list of checkboxes. */
  setMembers: (name: string, members: string[]) =>
    http.put<string[]>(path(name, '/members'), { members }),

  /* Slots */

  listSlots: (name: string) => http.get<EnsembleSlotModel[]>(path(name, '/slots')),

  addSlot: (name: string, slot: { weekday: number; time: string; durationMinutes?: number }) =>
    http.post<EnsembleSlotModel>(path(name, '/slots'), slot),

  updateSlot: (id: number, patch: Partial<{ weekday: number; time: string; durationMinutes: number }>) =>
    http.patch<EnsembleSlotModel>(`/ensemble-slots/${id}`, patch),

  removeSlot: (id: number) => http.delete<OkBody>(`/ensemble-slots/${id}`),

  /* Money */

  /**
   * What each member would be charged for one meeting, without booking one.
   *
   * Worked out on the server rather than here, so the figures the tutor
   * confirms are the figures that get written, down to which member receives
   * the remainder shekel of an uneven split.
   */
  charges: (name: string) => http.get<MemberChargeModel[]>(path(name, '/charges')),

  /** One meeting: one ordinary lesson per paying member, written as a unit. */
  schedule: (
    name: string,
    meeting: { date: string; time?: string | null; status?: string }
  ) => http.post<ScheduledMeetingModel>(path(name, '/meetings'), meeting),

  lessons: (name: string) => http.get<LessonModel[]>(path(name, '/lessons'))
};
